import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Button, Typography, Space, Alert } from "antd";
import dayjs, { Dayjs } from "dayjs";

const { Text } = Typography;

interface CloseInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (closeDate: string) => void;
  maturityDate: string;
  isLoading?: boolean;
}

const CloseInvestmentModal: React.FC<CloseInvestmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  maturityDate,
  isLoading,
}) => {
  const [closeDate, setCloseDate] = useState<Dayjs | null>(dayjs());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCloseDate(dayjs());
      setError(null);
    }
  }, [isOpen]);

  const getMinDate = (): Dayjs | undefined => {
    if (!maturityDate) return undefined;
    return dayjs(maturityDate.substring(0, 10));
  };

  const handleConfirm = () => {
    if (!closeDate) {
      setError("Please select a close date");
      return;
    }

    const minDate = getMinDate();
    if (minDate && closeDate.isBefore(minDate, "day")) {
      setError(
        `Close date must be on or after the maturity date (${minDate.format("YYYY-MM-DD")})`,
      );
      return;
    }

    setError(null);
    onConfirm(closeDate.format("YYYY-MM-DD"));
  };

  const handleDateChange = (date: Dayjs | null) => {
    setCloseDate(date);
    setError(null);
  };

  const disabledDate = (current: Dayjs): boolean => {
    const minDate = getMinDate();
    if (!minDate) return false;
    return current.isBefore(minDate, "day");
  };

  return (
    <Modal
      title="Close Investment"
      open={isOpen}
      onCancel={onClose}
      centered
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            danger
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!closeDate}
          >
            Confirm
          </Button>
        </Space>
      }
    >
      <div style={{ padding: "16px 0" }}>
        <Text>Please select the close date for this investment.</Text>

        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Close Date
          </Text>
          <DatePicker
            value={closeDate}
            onChange={handleDateChange}
            disabledDate={disabledDate}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            placeholder="Select close date"
          />
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

export default CloseInvestmentModal;
